import React from 'react';
import { AlertTriangle, Info, CheckCircle, XCircle, X } from 'lucide-react';
import './ConfirmationDialog.css';

export type DialogType = 'info' | 'warning' | 'success' | 'error' | 'confirm';
export type DialogAction = 'ok' | 'cancel' | 'yes' | 'no' | 'confirm' | 'delete';

export interface DialogButton {
  label: string;
  action: DialogAction;
  variant?: 'primary' | 'secondary' | 'danger';
  onClick: () => void;
}

export interface ConfirmationDialogProps {
  isOpen: boolean;
  type: DialogType;
  title: string;
  message: string;
  details?: string[];
  buttons: DialogButton[];
  onClose: () => void;
}

const typeIcons = {
  info: Info,
  warning: AlertTriangle,
  success: CheckCircle,
  error: XCircle,
  confirm: AlertTriangle,
};

const typeColors = {
  info: '#3b82f6',
  warning: '#f59e0b',
  success: '#10b981',
  error: '#ef4444',
  confirm: '#f59e0b',
};

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  type,
  title,
  message,
  details,
  buttons,
  onClose,
}) => {
  if (!isOpen) return null;

  const IconComponent = typeIcons[type];
  const iconColor = typeColors[type];

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getButtonClass = (variant?: string) => {
    switch (variant) {
      case 'primary':
        return 'dialog-button dialog-button-primary';
      case 'danger':
        return 'dialog-button dialog-button-danger';
      default:
        return 'dialog-button dialog-button-secondary';
    }
  };

  return (
    <div className="dialog-backdrop" onClick={handleBackdropClick}>
      <div className="dialog-container">
        <div className="dialog-header">
          <div className="dialog-icon" style={{ color: iconColor }}>
            <IconComponent size={24} />
          </div>
          <h3 className="dialog-title">{title}</h3>
          <button
            onClick={onClose}
            className="dialog-close"
            aria-label="Close dialog"
          >
            <X size={20} />
          </button>
        </div>

        <div className="dialog-content">
          <p className="dialog-message">{message}</p>
          
          {details && details.length > 0 && (
            <div className="dialog-details">
              <ul>
                {details.map((detail, index) => (
                  <li key={index}>{detail}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="dialog-actions">
          {buttons.map((button, index) => (
            <button
              key={index}
              onClick={button.onClick}
              className={getButtonClass(button.variant)}
            >
              {button.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Utility functions for common dialog types
export const useConfirmationDialog = () => {
  const [dialog, setDialog] = React.useState<{
    isOpen: boolean;
    props: Omit<ConfirmationDialogProps, 'isOpen'>;
  }>({
    isOpen: false,
    props: {
      type: 'info',
      title: '',
      message: '',
      buttons: [],
      onClose: () => {},
    },
  });

  const showDialog = (props: Omit<ConfirmationDialogProps, 'isOpen'>) => {
    setDialog({
      isOpen: true,
      props: {
        ...props,
        onClose: () => {
          setDialog(prev => ({ ...prev, isOpen: false }));
          props.onClose?.();
        },
      },
    });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  // Helper methods for common dialog types
  const confirm = (
    title: string,
    message: string,
    onConfirm: () => void,
    details?: string[]
  ) => {
    showDialog({
      type: 'confirm',
      title,
      message,
      details,
      buttons: [
        {
          label: 'Cancel',
          action: 'cancel',
          variant: 'secondary',
          onClick: closeDialog,
        },
        {
          label: 'Confirm',
          action: 'confirm',
          variant: 'primary',
          onClick: () => {
            onConfirm();
            closeDialog();
          },
        },
      ],
      onClose: closeDialog,
    });
  };

  const confirmDelete = (
    title: string,
    message: string,
    onDelete: () => void,
    details?: string[]
  ) => {
    showDialog({
      type: 'warning',
      title,
      message,
      details,
      buttons: [
        {
          label: 'Cancel',
          action: 'cancel',
          variant: 'secondary',
          onClick: closeDialog,
        },
        {
          label: 'Delete',
          action: 'delete',
          variant: 'danger',
          onClick: () => {
            onDelete();
            closeDialog();
          },
        },
      ],
      onClose: closeDialog,
    });
  };

  const alert = (
    type: DialogType,
    title: string,
    message: string,
    details?: string[]
  ) => {
    showDialog({
      type,
      title,
      message,
      details,
      buttons: [
        {
          label: 'OK',
          action: 'ok',
          variant: 'primary',
          onClick: closeDialog,
        },
      ],
      onClose: closeDialog,
    });
  };

  return {
    dialog: dialog.isOpen ? { isOpen: true, ...dialog.props } : { isOpen: false },
    confirm,
    confirmDelete,
    alert,
    showDialog,
    closeDialog,
  };
};
